import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { produce } from "immer";
import moment from "moment";
import React from "react";
import Modal, { CommonModalProps } from "..";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { DbMetadataFieldValueInfos } from "../../../Core/Api/Types/db-metadata-field-value-infos";
import { MetadataFieldValueCardinality } from "../../../Core/Api/Types/metadata-field-value-cardinality";
import { MetadataFieldValueObjectTypes } from "../../../Core/Api/Types/metadata-field-value-object-types";
import { GetObjectStatusLabel } from "../../../Utils/helpers";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { TitleType } from "../../../Core/Api/Types/title-type";
import { ComputeValueBaseTypeFromMetaBaseType, GetValueBaseTypeFromMetaBaseType } from "../../../Core/Utils/functions";
import FormSection from "../../Forms/FormSection/form-section";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import RatingDescValuesForm from "./rating-desc-values-form";
import GenreValuesForm from "./genre-values-form";
import RatingReasonValueForm from "./rating-reason-values-form";
import { StringMultiByEpisodeMetadataInfos } from "../../../Core/Api/Types/string-multi-by-episode-metadata-infos";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalCreateMetadataElementProps = {
  type: string;
  metadata?: DbMetadataFieldInfos;
  onClose?: (props: CloseClbProps) => void;
  onConfirm: (metadata: DbMetadataFieldInfos, type: string) => void;
};
export type ModalCreateMetadataProps = CommonModalProps<ModalCreateMetadataElementProps>;

const ModalCreateMetadata = React.forwardRef<any, ModalCreateMetadataProps>((props, ref) => {
  const { closeModal, onClose, type, metadata, onConfirm, ...modalProps } = props;
  const { t } = useTranslation();
  const [newMetadata, setNewMetadata] = React.useState<DbMetadataFieldInfos>(
    new DbMetadataFieldInfos({
      isEditable: false,
      isInternal: false,
      isMandatory: false,
      isSearchable: false,
      isUsedOnBundleMaker: false,
      allowPopulate: false,
      disabled: false,
      values: [],
    })
  );
  const [separator, setSeparator] = React.useState<string>("/");
  const [split, setSplit] = React.useState<string>("|");
  const [inputValues, setInputValues] = React.useState<string>("");
  const [isInputValueValid, setIsInputValueValid] = React.useState<boolean>(false);
  const [label, setLabel] = React.useState<string>("");
  const [value, setValue] = React.useState<string>("");
  const [rfc, setRfc] = React.useState<string>("");
  const [externalId, setExternalId] = React.useState<number | null>(null);
  const [showWarning, setShowWarning] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (metadata) setNewMetadata(metadata);
  }, [type, metadata]);

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  const CopyToclipboardValues = () => {
    if (!newMetadata.values || newMetadata.values.length === 0) return;

    let jsonOfValues = JSON.stringify(newMetadata.values);
    navigator.clipboard.writeText(jsonOfValues);
  };

  const handleConfirmClick = () => {
    onConfirm(newMetadata, type);
    onClose && onClose({ confirm: true });
    closeModal();
  };

  React.useEffect(() => {
    let isValid: boolean = true;
    if (inputValues !== "") {
      let values: string[] = inputValues.split(split);
      values.forEach((x) => {
        let tmp: string[] = x.split(separator);
        if (
          tmp.length !== 4 ||
          (tmp[3].length > 0 && Number.isNaN(Number(tmp[3]))) ||
          tmp[0].length === 0 ||
          tmp[1].length === 0
        ) {
          isValid = false;
        }
      });
    } else if (label === "" || value === "") isValid = false;
    if (inputValues !== "" && (label !== "" || value !== "") /*|| rfc !== "" || externalId !== null*/) isValid = false;
    if (isInputValueValid !== isValid) setIsInputValueValid(isValid);
  }, [inputValues, separator, split, value, label, externalId, rfc]);

  const setValueLabel = (baseType: BaseMetadataType, label: string): string | null => {
    if (baseType == BaseMetadataType.DbLanguageCodeMetadataValueInfos) return label;
    return null;
  };

  const setValueToObject = (baseType: BaseMetadataType, value: string, label: string) => {
    if (baseType === BaseMetadataType.DbLanguageCodeMetadataValueInfos) return { value: value, label: label };
    if (baseType === BaseMetadataType.DbStringMultiByEpisodeMetadataValueInfos) {
      const result: StringMultiByEpisodeMetadataInfos = {
        episodeNb: -1,
        value: [value],
      };
      return [result];
    }
    return value;
  };

  const setValueFromInput = (baseType: BaseMetadataType, value: string) => {
    if (baseType === BaseMetadataType.DbStringMultiByEpisodeMetadataValueInfos) {
      return `-1 (${value})`;
    }
    return value;
  };

  const onAddValuesClick = () => {
    if (inputValues !== "") {
      let values: string[] = inputValues.split(split);
      let newValues: DbMetadataFieldValueInfos[] = [];
      values.forEach((x) => {
        let tmp: string[] = x.split(separator);
        newValues.push({
          label: tmp[0],
          value: setValueFromInput(newMetadata.baseType!, tmp[1]),
          rfc5646: tmp[2],
          externalId: Number(tmp[3]) || -500,
          id: -500,
          externalValue: null,
          valueObject: {
            $type: ComputeValueBaseTypeFromMetaBaseType(newMetadata.baseType!),
            fieldType: GetValueBaseTypeFromMetaBaseType(newMetadata.baseType!),
            value: setValueToObject(newMetadata.baseType!, tmp[1], label),
            id: -500,
            creationTime: moment().toDate(),
            fieldLabel: null,
            fieldName: null,
            targetLayer: null,
            updateTime: null,
            valueLabel: setValueLabel(newMetadata.baseType!, label),
          },
        });
      });
      setNewMetadata(
        produce((draft) => {
          draft!.values = newValues;
        })
      );
    } else if (newMetadata.values?.find((x) => x.value === value) === undefined) {
      setNewMetadata(
        produce((draft) => {
          if (!draft.values) draft.values = [];
          draft!.values?.push({
            label: label,
            value: setValueFromInput(newMetadata.baseType!, value),
            rfc5646: rfc,
            externalId: externalId || -500,
            id: -500,
            externalValue: null,
            valueObject: {
              $type: ComputeValueBaseTypeFromMetaBaseType(newMetadata.baseType!),
              fieldType: GetValueBaseTypeFromMetaBaseType(newMetadata.baseType!),
              value: setValueToObject(newMetadata.baseType!, value, label),
              id: -500,
              creationTime: moment().toDate(),
              fieldLabel: null,
              fieldName: null,
              targetLayer: null,
              updateTime: null,
              valueLabel: setValueLabel(newMetadata.baseType!, label),
            },
          });
        })
      );
      if (showWarning) setShowWarning(false);
    } else if (!showWarning) setShowWarning(true);
  };

  return (
    <Modal
      {...modalProps}
      title={t("common:typeMetadata", { type })}
      reference={ref}
      closeModal={handleCancelClick}
      width={1200}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmClick}
            disabled={
              newMetadata?.label === null ||
              newMetadata.tagName === null ||
              newMetadata.baseType === null ||
              newMetadata.objectType === null ||
              newMetadata.layer === null ||
              newMetadata.valuesCardinality === null
            }
          >
            {t("common:typeMetadata", { type })}
          </Button>
        </Stack>
      }
    >
      <FormSection title={t("common:generalInformationsLowerCase")} status="todo" sx={{ mb: 1 }}>
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <InputLabel sx={{ mb: 1 }}>{t("common:labelUpperCase")}</InputLabel>
            <OutlinedInput
              value={newMetadata.label || ""}
              id={"label"}
              sx={{ width: "100%", mb: 2 }}
              onChange={(e) =>
                setNewMetadata(
                  produce((draft) => {
                    draft!.label = e.target.value;
                  })
                )
              }
            />
          </Grid>
          <Grid item xs={3}>
            <InputLabel sx={{ mb: 1 }}>{t("field:label.tagName")}</InputLabel>
            <OutlinedInput
              value={newMetadata.tagName || ""}
              id={"dalet"}
              sx={{ width: "100%", mb: 2 }}
              onChange={(e) =>
                setNewMetadata(
                  produce((draft) => {
                    draft!.tagName = e.target.value;
                  })
                )
              }
            />
          </Grid>
          <Grid item xs={3}>
            <InputLabel sx={{ mb: 1 }}>{t("field:label.baseType")}</InputLabel>
            <Select
              IconComponent={ExpandMoreRoundedIcon}
              value={newMetadata.baseType || ""}
              sx={{ width: "100%" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              onChange={(e) => {
                setNewMetadata(
                  produce((draft) => {
                    draft!.baseType = BaseMetadataType[e.target.value];
                    draft!.values =
                      newMetadata.values
                        ?.filter((x) => x)
                        .map((x) => {
                          return {
                            ...x!,
                            valueObject: {
                              ...x!.valueObject!,
                              $type: ComputeValueBaseTypeFromMetaBaseType(BaseMetadataType[e.target.value]),
                              fieldType: GetValueBaseTypeFromMetaBaseType(BaseMetadataType[e.target.value]),
                            },
                          };
                        }) ?? [];
                  })
                );
              }}
              disabled={metadata !== null && metadata !== undefined}
            >
              {Object.keys(BaseMetadataType).map((baseType) => (
                <MenuItem value={baseType} key={baseType}>
                  {baseType}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item md={3}>
            <InputLabel sx={{ mb: 1 }}>{t("common:objectType")}</InputLabel>
            <Select
              IconComponent={ExpandMoreRoundedIcon}
              value={newMetadata.objectType || ""}
              sx={{ width: "100%", mb: 2 }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              onChange={(e) => {
                setNewMetadata(
                  produce((draft) => {
                    draft!.objectType = MetadataFieldValueObjectTypes[e.target.value];
                  })
                );
              }}
            >
              {Object.keys(MetadataFieldValueObjectTypes).map((objectType) => (
                <MenuItem value={objectType} key={objectType}>
                  {objectType}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={4}>
            <InputLabel sx={{ mb: 1 }}>{t("field:label.objectLayer")}</InputLabel>
            <Select
              IconComponent={ExpandMoreRoundedIcon}
              value={newMetadata.layer || ""}
              sx={{ width: "100%", mb: 2 }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              onChange={(e) => {
                setNewMetadata(
                  produce((draft) => {
                    draft!.layer = ObjectLayer[e.target.value];
                  })
                );
              }}
            >
              {Object.keys(ObjectLayer).map((objectLayer) => (
                <MenuItem value={objectLayer} key={objectLayer}>
                  {objectLayer}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={4}>
            <InputLabel sx={{ mb: 1 }}>{t("field:label.cardinality")}</InputLabel>
            <Select
              IconComponent={ExpandMoreRoundedIcon}
              value={newMetadata.valuesCardinality || ""}
              sx={{ width: "100%", mb: 2 }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              onChange={(e) => {
                setNewMetadata(
                  produce((draft) => {
                    draft!.valuesCardinality = MetadataFieldValueCardinality[e.target.value];
                  })
                );
              }}
            >
              {Object.keys(MetadataFieldValueCardinality).map((cadinality) => (
                <MenuItem value={cadinality} key={cadinality}>
                  {cadinality}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={4}>
            <InputLabel sx={{ mb: 1 }}>{t("common:group")}</InputLabel>
            <OutlinedInput
              value={newMetadata.group || ""}
              id={"group"}
              sx={{ width: "100%", mb: 2 }}
              onChange={(e) =>
                setNewMetadata(
                  produce((draft) => {
                    draft!.group = e.target.value;
                  })
                )
              }
            />
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              sx={{ mb: 2 }}
              control={
                <Switch
                  size="small"
                  checked={newMetadata.isSearchable || false}
                  onChange={(e) =>
                    setNewMetadata(
                      produce((draft) => {
                        draft!.isSearchable = e.target.checked;
                      })
                    )
                  }
                />
              }
              label={t("field:label.isItSearchable")}
            />
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              sx={{ mb: 2 }}
              control={
                <Switch
                  size="small"
                  checked={newMetadata.isEditable || false}
                  onChange={(e) =>
                    setNewMetadata(
                      produce((draft) => {
                        draft!.isEditable = e.target.checked;
                      })
                    )
                  }
                />
              }
              label={t("field:label.isItEditable")}
            />
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              sx={{ mb: 2 }}
              control={
                <Switch
                  size="small"
                  checked={newMetadata.isMandatory || false}
                  onChange={(e) =>
                    setNewMetadata(
                      produce((draft) => {
                        draft!.isMandatory = e.target.checked;
                      })
                    )
                  }
                />
              }
              label={t("field:label.isItMandatory")}
            />
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              sx={{ mb: 2 }}
              control={
                <Switch
                  size="small"
                  checked={newMetadata.isInternal || false}
                  onChange={(e) =>
                    setNewMetadata(
                      produce((draft) => {
                        draft!.isInternal = e.target.checked;
                      })
                    )
                  }
                />
              }
              label={t("field:label.isItInternal")}
            />
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              sx={{ mb: 2 }}
              control={
                <Switch
                  size="small"
                  checked={newMetadata.isUsedOnBundleMaker || false}
                  onChange={(e) =>
                    setNewMetadata(
                      produce((draft) => {
                        draft!.isUsedOnBundleMaker = e.target.checked;
                      })
                    )
                  }
                />
              }
              label={t("field:label.isUsedOnBundleMaker")}
            />
          </Grid>
          {newMetadata.baseType === BaseMetadataType.DbBoolMetadataValueInfos && (
            <Grid item xs={2}>
              <FormControlLabel
                sx={{ mb: 2 }}
                control={
                  <Switch
                    size="small"
                    checked={newMetadata.prohibitTrueToFalse}
                    onChange={(e) =>
                      setNewMetadata(
                        produce((draft) => {
                          draft!.prohibitTrueToFalse = e.target.checked;
                        })
                      )
                    }
                  />
                }
                label={t("field:label.booleanProhibit")}
              />
            </Grid>
          )}
          {newMetadata.baseType === BaseMetadataType.DbBoolMetadataValueInfos && (
            <Grid item xs={2}>
              <FormControlLabel
                sx={{ mb: 2 }}
                control={
                  <Switch
                    size="small"
                    checked={newMetadata.indexFalseBool ?? false}
                    onChange={(e) =>
                      setNewMetadata(
                        produce((draft) => {
                          draft!.indexFalseBool = e.target.checked;
                        })
                      )
                    }
                  />
                }
                label={t("field:label.booleanStoreFalse")}
              />
            </Grid>
          )}
          {(newMetadata.baseType === BaseMetadataType.DbStringMetadataValueInfos ||
            newMetadata.baseType === BaseMetadataType.DbStringMultiMetadataValueInfos) &&
            (newMetadata.layer === ObjectLayer.Title || newMetadata.objectClasses?.includes(ObjectClass.Drive)) && (
              <Grid item xs={2}>
                <FormControlLabel
                  sx={{ mb: 2 }}
                  control={
                    <Switch
                      size="small"
                      checked={newMetadata.searchPatternEnabled || false}
                      onChange={(e) =>
                        setNewMetadata(
                          produce((draft) => {
                            draft!.searchPatternEnabled = e.target.checked;
                          })
                        )
                      }
                    />
                  }
                  label={t("form:label.enableQuickSearch")}
                />
              </Grid>
            )}
          {(newMetadata.baseType === BaseMetadataType.DbLongMetadataValueInfos ||
            newMetadata.baseType === BaseMetadataType.DbLongMultiMetadataValueInfos ||
            newMetadata.baseType === BaseMetadataType.DbStringMultiMetadataValueInfos ||
            newMetadata.baseType === BaseMetadataType.DbDoubleMultiMetadataValueInfos ||
            newMetadata.baseType === BaseMetadataType.DbStringMetadataValueInfos ||
            newMetadata.baseType === BaseMetadataType.DbDoubleMetadataValueInfos ||
            newMetadata.baseType === BaseMetadataType.DbStringMultiByEpisodeMetadataValueInfos) && (
            <Grid item xs={2}>
              <FormControlLabel
                sx={{ mb: 2 }}
                control={
                  <Switch
                    size="small"
                    checked={newMetadata.allowPopulate || false}
                    onChange={(e) =>
                      setNewMetadata(
                        produce((draft) => {
                          draft!.allowPopulate = e.target.checked;
                        })
                      )
                    }
                  />
                }
                label={t("field:label.allowPopulatePredefinedValues")}
              />
            </Grid>
          )}
          <Grid item xs={4}>
            <InputLabel sx={{ mb: 1 }}>{t("field:label.titleType")}</InputLabel>
            <Select
              IconComponent={ExpandMoreRoundedIcon}
              value={newMetadata.titleType || ""}
              sx={{ width: "100%", mb: 2 }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              onChange={(e) => {
                setNewMetadata(
                  produce((draft) => {
                    draft!.titleType = TitleType[e.target.value];
                  })
                );
              }}
            >
              {Object.keys(TitleType).map((titleType) => (
                <MenuItem value={titleType} key={titleType}>
                  {titleType}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={4}>
            <InputLabel sx={{ mb: 1 }}>{t("field:label.objectClasses")}</InputLabel>
            <Autocomplete
              multiple
              value={newMetadata.objectClasses || []}
              onChange={(e, value) =>
                setNewMetadata(
                  produce((draft) => {
                    draft!.objectClasses = value as ObjectClass[];
                  })
                )
              }
              options={Object.keys(ObjectClass)}
              renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title={t("form:section.title.filteringRules")} status="todo" sx={{ mb: 1 }}>
        <Grid container spacing={3}>
          <Grid item md={6}>
            <InputLabel sx={{ mb: 1 }}>{t("field:label.applyOnAssetTypes")}</InputLabel>
            <Autocomplete
              multiple
              options={Object.values(ObjectType)}
              value={newMetadata?.objectTypes || []}
              popupIcon={<ExpandMoreRoundedIcon />}
              renderInput={(params) => (
                <OutlinedInput
                  {...params.InputProps}
                  inputProps={params.inputProps}
                  required
                  sx={{ width: "100%", mb: 2 }}
                />
              )}
              sx={{ width: "100%" }}
              id={"objectTypeFilter"}
              onChange={(e, value) => {
                setNewMetadata(
                  produce((draft) => {
                    draft!.objectTypes = value;
                  })
                );
              }}
            />
          </Grid>
          <Grid item md={6}>
            <InputLabel sx={{ mb: 1 }}>{t("field:label.applyOnAssetStatuses")}</InputLabel>
            <Autocomplete
              multiple
              options={Object.values(ObjectStatus)}
              getOptionLabel={(options: ObjectStatus) => GetObjectStatusLabel(options)}
              value={newMetadata?.objectStatuses || []}
              popupIcon={<ExpandMoreRoundedIcon />}
              renderInput={(params) => (
                <OutlinedInput
                  {...params.InputProps}
                  inputProps={params.inputProps}
                  required
                  sx={{ width: "100%", mb: 2 }}
                />
              )}
              sx={{ width: "100%" }}
              id={"objectStatusFilter"}
              onChange={(e, value) => {
                setNewMetadata(
                  produce((draft) => {
                    draft!.objectStatuses = value;
                  })
                );
              }}
            />
          </Grid>
        </Grid>
      </FormSection>

      {newMetadata.baseType === BaseMetadataType.DbRatingDescriptorMetadataValueInfos ? (
        <RatingDescValuesForm setMetadata={setNewMetadata} metadata={newMetadata} />
      ) : newMetadata.baseType === BaseMetadataType.DbGenreMultiMetadataValueInfos ? (
        <GenreValuesForm setMetadata={setNewMetadata} metadata={newMetadata} />
      ) : newMetadata.baseType === BaseMetadataType.DbLocalizedRatingReasonMultiMetadataValueInfos ? (
        <RatingReasonValueForm setMetadata={setNewMetadata} metadata={newMetadata} />
      ) : (
        <FormSection title={t("form:section.title.valuesDefinitions")} status="todo">
          <Grid container spacing={3}>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("common:separator")}</InputLabel>
              <OutlinedInput
                value={separator}
                id={"separator"}
                sx={{ width: "100%", mb: 2 }}
                onChange={(e) => setSeparator(e.target.value)}
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("common:split")}</InputLabel>
              <OutlinedInput
                value={split}
                id={"split"}
                sx={{ width: "100%", mb: 2 }}
                onChange={(e) => setSplit(e.target.value)}
              />
            </Grid>
            <Grid item md={12}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.postHereYourValues")}</InputLabel>
              <OutlinedInput
                value={inputValues}
                id={"values"}
                sx={{ width: "100%", mb: 2 }}
                placeholder="label/value/rfc5646/external-id|label/value/rfc5646/external-id|label/value/rfc5646/external-id|..."
                onChange={(e) => setInputValues(e.target.value)}
              />
            </Grid>

            <Grid item md={3}>
              <InputLabel sx={{ mb: 1 }}>{t("common:labelUpperCase")}</InputLabel>
              <OutlinedInput
                value={label}
                id={"valueLabel"}
                sx={{ width: "100%", mb: 2 }}
                placeholder={t("common:labelUpperCase")}
                onChange={(e) => setLabel(e.target.value)}
              />
            </Grid>
            <Grid item md={3}>
              <InputLabel sx={{ mb: 1 }}>{t("common:value")}</InputLabel>
              <OutlinedInput
                value={value}
                id={"valueValue"}
                sx={{ width: "100%", mb: 2 }}
                placeholder="Value"
                onChange={(e) => setValue(e.target.value)}
              />
            </Grid>
            <Grid item md={2}>
              <InputLabel sx={{ mb: 1 }}>Rfc5646</InputLabel>
              <OutlinedInput
                value={rfc}
                id={"valueRfc5646"}
                sx={{ width: "100%", mb: 2 }}
                placeholder="Rfc5646"
                onChange={(e) => setRfc(e.target.value)}
              />
            </Grid>
            <Grid item md={2}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.externalID")}</InputLabel>
              <OutlinedInput
                value={externalId || ""}
                id={"valueExternalId"}
                sx={{ width: "100%", mb: 2 }}
                placeholder="ExternalId"
                type="number"
                onChange={(e) => setExternalId(Number(e.target.value))}
              />
            </Grid>
            <Grid item md={2}>
              <InputLabel sx={{ mb: 1 }}>&nbsp;</InputLabel>
              <Button onClick={onAddValuesClick} color="success" disabled={!isInputValueValid}>
                {t("common:add")}
              </Button>
            </Grid>
            {showWarning && (
              <Grid item md={12}>
                {" "}
                <Alert color="error">{t("alert:error.valueAlreadyExists")}</Alert>
              </Grid>
            )}
            <Grid item md={12}>
              <InputLabel sx={{ mb: 1 }}>
                <Typography onClick={CopyToclipboardValues}>
                  {t("field:label.valueWithCount", { count: newMetadata.values?.length ?? 0 })}
                </Typography>
              </InputLabel>
              {newMetadata.values?.map((x, index) => {
                return (
                  <Tooltip
                    arrow
                    key={index}
                    title={t("field:label.externalID") + " : " + x.externalId + " - Rfc5646 : " + x.rfc5646}
                  >
                    <Chip
                      key={x.label}
                      variant="tag"
                      label={x.label + " (" + x.value + ")"}
                      size="medium"
                      color="primary"
                      onDelete={() =>
                        setNewMetadata(
                          produce((draft) => {
                            draft!.values = draft!.values?.filter((y) => y.label !== x.label) || [];
                          })
                        )
                      }
                    />
                  </Tooltip>
                );
              })}
            </Grid>
          </Grid>
        </FormSection>
      )}
      {newMetadata.platformId === null && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          {t("alert:warning.metadataIsSharedAcrossAllPlatforms")}
        </Alert>
      )}
    </Modal>
  );
});

export default ModalCreateMetadata;
